import React, { useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Rating from '@mui/material/Rating';
import Grid from '@mui/material/Grid'; 
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { useEvents } from '../contexts/EventContext';
import { useTenant } from '../contexts/TenantContext';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { useTranslation } from 'react-i18next';
import Badge from '@mui/material/Badge';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
// import Timeline from '@mui/lab/Timeline';
// import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
// import TimelineSeparator from '@mui/lab/TimelineSeparator';
// import TimelineConnector from '@mui/lab/TimelineConnector';
// import TimelineContent from '@mui/lab/TimelineContent';
// import TimelineDot from '@mui/lab/TimelineDot';
// // import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import CelebrationIcon from '@mui/icons-material/Celebration';

import ServiceCard from './ServiceCard';
import useMediaQuery from '@mui/material/useMediaQuery';

import EventGlobalRating from './EventGlobalRating';

// Ce composant reprend la structure de EventCard, mais est destiné à un affichage détaillé (fiche complète)
const EventDetailsCard = ({ event, handleEventClick, onCommentAdded }) => {
  const { t, i18n } = useTranslation();
  const { userData } = useUser();
  const { updateSingleEventStatus, fetchUserEventsV2, updateEventRating } = useEvents();
  const { selectedTenantId } = useTenant();
  const theme = useTheme();
  const [hover, setHover] = useState(-1);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [pendingRating, setPendingRating] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const labels = {
    1: t('eventCard.rating.useless'),
    2: t('eventCard.rating.poor'),
    3: t('eventCard.rating.ok'),
    4: t('eventCard.rating.good'),
    5: t('eventCard.rating.excellent'),
  };

  useEffect(() => {
    setRating(event.event_user_state?.feedback_value || 0);
  }, [event.event_user_state?.feedback_value]);

  function orderServicesByStartTime(services) {
    // Check if services has a start_time property. If not display at the end of the list
    const hasStartTime = services.filter(service => service.start_time);
    // const noStartTime = services.filter(service => !service.start_time);
    // return [...hasStartTime.sort((a, b) => new Date(a.start_time) - new Date(b.start_time)), ...noStartTime];
    return [...hasStartTime.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))];
  }

  function getServicesWithoutDateTime(services) {
    return services.filter(service => !service.start_time);
  }

  function getLabelText(value) {
    const label = labels[value];
    return `${value} ${t('eventCard.rating.banana', { count: value })}${label ? `, ${label}` : ''}`;
  }

  const formatTime = (dateString) => {
    if (!dateString) return t('eventCard.formatDateNA');
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      console.error('Error formatting time:', error);
      return dateString;
    }
  };

  // Helper function to format date exactly like EventCard
  const formatDate = (dateString) => {
    if (!dateString) return t('eventCard.formatDateNA');
    try {
      const date = new Date(dateString);
      // Format: Vendredi 27 juin 2025 à 18:00
      const day = date.toLocaleDateString(i18n.language, { weekday: 'long' });
      const dayNum = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString(i18n.language, { month: 'long' });
      const year = date.getFullYear();
      const hour = date.getHours().toString().padStart(2, '0');
      const minute = date.getMinutes().toString().padStart(2, '0');
      return `${day.charAt(0).toUpperCase() + day.slice(1)} ${dayNum} ${month} ${year} à ${hour}:${minute}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const getCalendarDate = (isoString) => {
    if (!isoString) return undefined;
    return isoString.substring(0, 10);
  };
  const getCalendarTime = (isoString) => {
    if (!isoString) return '';
    return isoString.split('T')[1].substring(0, 5);
  };
  const formatDuration = (minutes) => {
    if (!minutes) return t('eventCard.unknown');
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      return t('eventCard.durationDays', { count: days, days });
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) {
      return t('eventCard.durationMinutes', { count: minutes, duration: minutes });
    } else {
      return t('eventCard.durationHours', { count: hours, hours, minutes: remainingMinutes > 0 ? remainingMinutes : '' });
    }
  };

  const handleCommentDialogClose = () => {
    setCommentDialogOpen(false);
    setPendingRating(null);
    setRating(null);
    setComment('');
  };

  const handleCommentSubmit = async () => {
    if (!pendingRating) return;
    setIsSubmitting(true); // Activer l'état de soumission
    const { event: eventToUpdate, rating } = pendingRating;
    if (!userData || !selectedTenantId) return;
    const originalRating = eventToUpdate.feedback?.value || null;
    updateEventRating(eventToUpdate.id, rating);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const payload = {
        feedback_value: rating,
        comment: comment
      };
      await axios.post(
        `${BACKEND_URL}/app/t:${selectedTenantId}/api/v2/events/e:${eventToUpdate.event_id}/user-states/feedback/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (onCommentAdded) onCommentAdded(); // Notifie le parent pour refresh
    } catch (error) {
      updateEventRating(eventToUpdate.event_id, originalRating);
    }
    setIsSubmitting(false); // Réinitialiser l'état de soumission
    handleCommentDialogClose();
  };

  const handleRatingChange = (eventToUpdate, newRating) => {
    if (!eventToUpdate) return;
    setPendingRating({ event: eventToUpdate, rating: newRating });
    setRating(newRating);
    setCommentDialogOpen(true);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Debugging: Test with a different breakpoint
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  console.log('isMobile:', isMobile, 'isTablet:', isTablet);

  if (!event) return null;

  const eventStartDate = new Date(event.start_time);
  const now = new Date();
  const isEventInThePast = eventStartDate < now;

  const calendarProps = {
    name: event.title,
    description: event.description || event.show?.description || '',
    options: ['Google', 'Outlook.com', 'Microsoft365', 'MicrosoftTeams'],
    location: event.location || '',
    timeZone: 'Europe/Paris',
    startDate: getCalendarDate(event.start_time),
    startTime: getCalendarTime(event.start_time),
    listStyle: 'overlay',
    language: i18n.language === 'fr' ? 'fr' : 'en',
  };
  if (event.end_time) {
    calendarProps.endDate = getCalendarDate(event.end_time);
    calendarProps.endTime = getCalendarTime(event.end_time);
  } else if (event.duration_minutes) {
    calendarProps.duration = event.duration_minutes;
  }

  return (
    <Card className="event-details-card" sx={{ width: '100%', maxWidth: 700, margin: '0 auto', mt: 4 }}>
      <CardMedia
        component="img"
        height="220"
        image={event.images || 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y29uY2VydHxlbnwwfHwwfHx8MA%3D%3D'}
        alt={event.title}
      />
      
      <CardContent>
        <Typography gutterBottom variant="h4" component="div" sx={{ fontWeight: 700 }}>
          {event.title}
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        {isMobile ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarTodayOutlinedIcon sx={{ mr: 1 }} />
              <Tooltip title={event.start_time} arrow>
                <Typography 
                  variant="body2"
                  color="text.secondary"
                  sx={{ 
                    textAlign: 'left', 
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    display: 'block'
                  }}
                >
                  {formatDate(event.start_time)}
                </Typography>
              </Tooltip>
            </Box>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <AccessTimeOutlinedIcon sx={{ marginRight: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', marginRight: 2 }}>
                {formatDuration(event.duration_minutes)}
              </Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <LocationOnOutlinedIcon sx={{ marginRight: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                {event.location_details.space || 'Active'}
              </Typography>
            </div>
            <EventGlobalRating event={event} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item size={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayOutlinedIcon sx={{ mr: 1 }} />
                <Tooltip title={event.start_time} arrow>
                  <Typography 
                    variant="body2"
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'left', 
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      display: 'block'
                    }}
                  >
                    {formatDate(event.start_time)}
                  </Typography>
                </Tooltip>
              </Box>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <AccessTimeOutlinedIcon sx={{ marginRight: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', marginRight: 2 }}>
                  {formatDuration(event.duration_minutes)}
                </Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <LocationOnOutlinedIcon sx={{ marginRight: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                  {event.location_details.space || 'Active'}
                </Typography>
              </div>
            </Grid>
            <Grid item size={5}>
              <EventGlobalRating event={event} />
            </Grid>
          </Grid>
        )}
        
        { event.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left' }}>
              {event.description}
            </Typography>
          </>
        )}

        {((event.video_link) &&
            /(?:youtu.be\/|youtube.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/.test(event.video_link || event.show?.video_link)) && (
              <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
            <iframe
                width="100%"
                height="315"
                style={{ maxWidth: 560, borderRadius: 8, border: 0 }}
                src={`https://www.youtube.com/embed/${(event.video_link).match(/(?:youtu.be\/|youtube.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/)[1]}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
            </Box>
              </>
          )}

          <>
            {event.services && event.services.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <>
                    <Typography variant="h6" gutterBottom>{t('eventCard.servicesAllEventLong')}</Typography>
                    <Grid container spacing={2} className="events-grid-container" sx={{ alignItems: 'center', justifyContent: 'center' }}>
                      {getServicesWithoutDateTime(event.services).map((service, index) => (
                        <Grid item xs={12} sm={6} md={4} key={event.event_id}>
                          <ServiceCard key={index} service={service} />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('eventCard.servicesIncluded')}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {/* <Timeline position={!isMobile ? "alternate" : "right"} sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}> */}
                    <>
                    {!isMobile && (
                      <Timeline position="right" sx={{
                        [`& .${timelineItemClasses.root}:before`]: {
                          flex: 0,
                          padding: 0,
                        },
                        display: 'ruby'
                      }}>
                        {orderServicesByStartTime(event.services).map((service, index) => (
                            <>
                              <TimelineItem key={index}>
                                <TimelineSeparator>
                                  <TimelineDot>
                                    {(() => {
                                      switch (service.resourcetype) {
                                        case 'Concert':
                                          return <MusicNoteIcon />;
                                        case 'Catering':
                                          return <LocalDiningIcon />;
                                        case 'Exhibition':
                                          return <TheaterComedyIcon />;
                                        default:
                                          return <CelebrationIcon />; // Display a default icon for unknown resource types
                                      }
                                    })()}
                                  </TimelineDot>
                                  <TimelineConnector />
                                </TimelineSeparator>
                                <TimelineContent>
                                  <Typography sx={{ m: 'auto 0', pt: 1.6 }}
                                    variant="body2"
                                    color="text.secondary">
                                    {service.start_time && service.end_time
                                      ? `${formatTime(service.start_time)} - ${formatTime(service.end_time)}`
                                      : t('eventCard.fullEventDuration', "Pendant tout l'événement")}
                                  </Typography>
                                  <ServiceCard service={service} />
                                </TimelineContent>
                              </TimelineItem>
                              
                            </>
                        ))}
                      </Timeline>
                    )}

                    { isMobile && (
                      <>
                        {orderServicesByStartTime(event.services).map((service, index) => (
                          <ServiceCard service={service} />  
                        ))}
                      </>
                    )}
                    </>
                  </Box>
                </Box>
              </>
            )}
          </>
        

        { isEventInThePast && (
          <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{t('eventCard.shareRatingPrompt')}</Typography>
            <Tooltip 
              title={labels[hover !== -1 ? hover : event.feedback?.value] ? `${t('eventCard.itWas')} ${labels[hover !== -1 ? hover : event.feedback?.value]}` : ''}
              arrow
              placement="top"
            >
              <span style={{ cursor: 'pointer', display: 'inline-flex' }}>
                <Rating
                  name={`hover-feedback-${event.id}`}
                  // value={event.event_user_state?.feedback_value || 0}
                  value={rating}
                  precision={1}
                  emptyIcon={<span style={{ fontSize: '1.5rem' }}>🍌</span>}
                  icon={<span style={{ fontSize: '1.5rem' }}>🍌</span>}
                  getLabelText={getLabelText}
                  onChange={(eventObj, newValue) => {
                    if (typeof newValue === 'number' && newValue > 0) {
                      handleRatingChange(event, newValue);
                    }
                  }}
                  onChangeActive={(eventObj, newHover) => {
                    setHover(newHover);
                  }}
                />
              </span>
            </Tooltip>
          </Box>
          </>
        )}
        {/* Dialog pour commentaire après notation */}
        <Dialog
          open={commentDialogOpen}
          onClose={handleCommentDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { minWidth: 400, maxWidth: 700 } }}
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            {t('eventCard.ratingCommentTitle', 'Ajouter un commentaire')}
          </DialogTitle>
          <DialogContent>
            {pendingRating && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ mr: 1 }}>
                  {t('eventCard.ratingLabel', 'Note')} :
                </Typography>
                <Rating
                  value={pendingRating.rating}
                  onChange={(eventObj, newValue) => {
                    if (typeof newValue === 'number' && newValue > 0) {
                      handleRatingChange(event, newValue);
                    }
                  }}
                  icon={<span style={{ fontSize: '1.5rem' }}>🍌</span>}
                  emptyIcon={<span style={{ fontSize: '1.5rem' }}>🍌</span>}
                />
              </Box>
            )}
            <TextField
              autoFocus
              margin="dense"
              label={t('eventCard.ratingCommentLabel', 'Votre commentaire (optionnel)')}
              type="text"
              fullWidth
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSubmitting} // Désactiver le champ pendant la soumission
            />
            {isSubmitting && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('eventCard.submitting', 'Traitement en cours...')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCommentDialogClose} disabled={isSubmitting}>
              {t('eventCard.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleCommentSubmit} variant="contained" disabled={isSubmitting}>
              {isSubmitting ? t('eventCard.submitting', 'Traitement en cours...') : t('eventCard.submit', 'Valider')}
            </Button>
          </DialogActions>
        </Dialog>
        
      </CardContent>
    </Card>
  );
};

export default EventDetailsCard;
